import React, { useId, useMemo } from 'react';

type SleepPoint = {
  t?: number;
  value?: number;
};

export type SleepRun = {
  startT: number;
  endT: number;
  value: number;
  nextValue?: number;
  minuteCount: number;
};

export const SLEEP_STAGE_NAMES: Record<number, string> = {
  1: 'Deep',
  2: 'Light',
  3: 'REM',
  4: 'Awake',
};

export const SLEEP_STAGE_COLORS: Record<number, string> = {
  0: '#b0bec5',
  1: '#0d47a1',
  2: '#42a5f5',
  3: '#7e57c2',
  4: '#ffb300',
};

const MINUTES_PER_DAY = 24 * 60;
/** Evening start of the sleep window shown on daily columns (23:00). */
export const SLEEP_EVENING_MINUTES = 23 * 60;
/** Morning end of the sleep window (12:00). */
export const SLEEP_MORNING_MINUTES = 12 * 60;
/** Y-axis span: 23:00 → midnight → 12:00 (13 hours). */
export const SLEEP_DAILY_Y_SPAN =
  MINUTES_PER_DAY - SLEEP_EVENING_MINUTES + SLEEP_MORNING_MINUTES;

export const minutesOfDayToSleepWindowY = (minutesOfDay: number): number => {
  if (minutesOfDay >= SLEEP_EVENING_MINUTES) {
    return minutesOfDay - SLEEP_EVENING_MINUTES;
  }
  return minutesOfDay + (MINUTES_PER_DAY - SLEEP_EVENING_MINUTES);
};

export const sleepWindowYToMinutesOfDay = (y: number): number => {
  const eveningBand = MINUTES_PER_DAY - SLEEP_EVENING_MINUTES;
  if (y < eveningBand) {
    return y + SLEEP_EVENING_MINUTES;
  }
  return y - eveningBand;
};

export const clipModRangeToSleepWindow = (
  startMod: number,
  endMod: number
): Array<{ startMod: number; endMod: number }> => {
  if (endMod <= startMod) return [];
  const pieces: Array<{ startMod: number; endMod: number }> = [];
  const eveningStart = Math.max(startMod, SLEEP_EVENING_MINUTES);
  const eveningEnd = Math.min(endMod, MINUTES_PER_DAY);
  if (eveningEnd > eveningStart) {
    pieces.push({ startMod: eveningStart, endMod: eveningEnd });
  }
  const morningStart = Math.max(startMod, 0);
  const morningEnd = Math.min(endMod, SLEEP_MORNING_MINUTES);
  if (morningEnd > morningStart) {
    pieces.push({ startMod: morningStart, endMod: morningEnd });
  }
  return pieces;
};

export const sleepNightMidnightTForSegment = (dayMidnightT: number, startMod: number): number =>
  startMod <= SLEEP_MORNING_MINUTES ? dayMidnightT - MINUTES_PER_DAY : dayMidnightT;

export const snapSleepWideZoomToNight = (
  rangeStartDate: Date,
  rangeMinutes: number,
  from: number,
  to: number
): { from: number; to: number } => {
  const center = (from + to) / 2;
  const centerDate = new Date(rangeStartDate.getTime() + center * 60 * 1000);
  let nightDate = new Date(centerDate.getFullYear(), centerDate.getMonth(), centerDate.getDate());
  const minutesOfDay = centerDate.getHours() * 60 + centerDate.getMinutes();
  if (minutesOfDay <= SLEEP_MORNING_MINUTES) {
    nightDate.setDate(nightDate.getDate() - 1);
  }
  const nightMidnightT = (nightDate.getTime() - rangeStartDate.getTime()) / (60 * 1000);
  const zoomFrom = nightMidnightT + SLEEP_EVENING_MINUTES;
  const zoomTo = nightMidnightT + MINUTES_PER_DAY + SLEEP_MORNING_MINUTES;
  return {
    from: Math.max(0, zoomFrom),
    to: Math.min(rangeMinutes, zoomTo),
  };
};

const DAILY_COLUMN_WIDTH_RATIO = 0.72;
const MIN_DAILY_COLUMN_WIDTH = 8;
const MAX_DAILY_COLUMN_WIDTH = 36;

export const formatSleepDuration = (minutes: number): string => {
  const rounded = Math.max(0, Math.round(minutes));
  if (rounded < 60) return `${rounded} min`;
  const hours = Math.floor(rounded / 60);
  const mins = rounded % 60;
  return mins > 0 ? `${hours}h ${mins} min` : `${hours}h`;
};

const BAR_HEIGHT = 22;
/** 0 = gap only; 0.5 = bar center to bar center; 1 = full bar height overlap */
const CONNECTOR_REACH_RATIO = 0.6;
const CONNECTOR_WIDTH_RATIO = 0.16;
const CONNECTOR_BLUR = 0.45;
const MIN_BAR_WIDTH = 6;
const CONNECTOR_COLOR = '#90CAF9';
const MAX_CONNECT_GAP_MINUTES = 5;

const pointGapMinutes = (fromT: number, toT: number): number =>
  toT - fromT >= 0 ? toT - fromT : toT - fromT + 24 * 60;

/** Vertical connector between two stage bars; reach controlled by CONNECTOR_REACH_RATIO. */
const connectorSpan = (y0: number, y1: number): { top: number; height: number } => {
  const upper = Math.min(y0, y1);
  const lower = Math.max(y0, y1);
  const inset = (BAR_HEIGHT / 2) * (1 - CONNECTOR_REACH_RATIO);
  const top = upper + inset;
  const bottom = lower - inset;
  const height = Math.max(0, bottom - top);
  return { top, height };
};

export const buildSleepRuns = (data: SleepPoint[]): SleepRun[] => {
  const validPoints = data.filter(
    (point) => point?.t != null && point?.value != null && !Number.isNaN(Number(point.value))
  );
  if (validPoints.length === 0) return [];

  const runs: SleepRun[] = [];
  let index = 0;
  while (index < validPoints.length) {
    let runEnd = index;
    while (
      runEnd + 1 < validPoints.length &&
      validPoints[runEnd + 1].value === validPoints[index].value &&
      pointGapMinutes(
        validPoints[runEnd].t as number,
        validPoints[runEnd + 1].t as number
      ) <= MAX_CONNECT_GAP_MINUTES
    ) {
      runEnd += 1;
    }

    const lastT = validPoints[runEnd].t as number;
    let nextPoint: SleepPoint | undefined;
    if (runEnd + 1 < validPoints.length) {
      const candidate = validPoints[runEnd + 1];
      const gap = pointGapMinutes(lastT, candidate.t as number);
      if (gap <= MAX_CONNECT_GAP_MINUTES) {
        nextPoint = candidate;
      }
    }

    runs.push({
      startT: validPoints[index].t as number,
      endT: nextPoint?.t ?? lastT,
      value: validPoints[index].value as number,
      nextValue: nextPoint?.value as number | undefined,
      minuteCount: runEnd - index + 1,
    });
    index = runEnd + 1;
  }
  return runs;
};

export type SleepDailySegment = {
  dayMidnightT: number;
  startMod: number;
  endMod: number;
  value: number;
  run: SleepRun;
};

/** Split a stage run into calendar-day slices (minutes-of-day on each slice). */
export const splitRunIntoDailySegments = (
  rangeStartMs: number,
  run: SleepRun
): SleepDailySegment[] => {
  const endT = run.endT > run.startT ? run.endT : run.startT + run.minuteCount;
  let cursorMs = rangeStartMs + run.startT * 60 * 1000;
  const endMs = rangeStartMs + endT * 60 * 1000;
  if (cursorMs >= endMs) return [];

  const segments: SleepDailySegment[] = [];
  while (cursorMs < endMs) {
    const d = new Date(cursorMs);
    const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const dayEndMs = dayStart.getTime() + MINUTES_PER_DAY * 60 * 1000;
    const segEndMs = Math.min(endMs, dayEndMs);
    const startMod = (cursorMs - dayStart.getTime()) / (60 * 1000);
    const endMod = (segEndMs - dayStart.getTime()) / (60 * 1000);
    const dayMidnightT = (dayStart.getTime() - rangeStartMs) / (60 * 1000);
    if (endMod > startMod) {
      segments.push({
        dayMidnightT,
        startMod,
        endMod,
        value: run.value,
        run,
      });
    }
    cursorMs = segEndMs;
  }
  return segments;
};

const stageColor = (value: number): string =>
  SLEEP_STAGE_COLORS[value] ?? SLEEP_STAGE_COLORS[2];

type SleepStageChartProps = {
  data?: SleepPoint[];
  xAxisMap?: Record<string, unknown>;
  yAxisMap?: Record<string, unknown>;
  showConnectors?: boolean;
  layoutMode?: 'stage' | 'daily';
  rangeStartDate?: Date;
  visibleMinT?: number;
  visibleMaxT?: number;
  onRunHover?: (run: SleepRun | null, event: React.MouseEvent<Element>) => void;
};

export const SleepStageChart = (props: SleepStageChartProps) => {
  const {
    data,
    xAxisMap,
    yAxisMap,
    showConnectors = true,
    layoutMode = 'stage',
    rangeStartDate,
    visibleMinT,
    visibleMaxT,
    onRunHover,
  } = props;
  const chartId = useId().replace(/:/g, '');
  const runs = useMemo(() => (Array.isArray(data) ? buildSleepRuns(data) : []), [data]);
  const isDailyLayout = layoutMode === 'daily' && rangeStartDate instanceof Date;
  const rangeStartMs = rangeStartDate instanceof Date ? rangeStartDate.getTime() : 0;

  const dailySegments = useMemo(() => {
    if (!isDailyLayout) return [];
    return runs.flatMap((run) => splitRunIntoDailySegments(rangeStartMs, run));
  }, [isDailyLayout, runs, rangeStartMs]);

  const dailyDrawSegments = useMemo(() => {
    if (!isDailyLayout) return [];
    return dailySegments.flatMap((seg) => {
      const columnNightT = sleepNightMidnightTForSegment(seg.dayMidnightT, seg.startMod);
      return clipModRangeToSleepWindow(seg.startMod, seg.endMod).map((clip) => ({
        columnNightT,
        startMod: clip.startMod,
        endMod: clip.endMod,
        value: seg.value,
        run: seg.run,
      }));
    });
  }, [isDailyLayout, dailySegments]);

  const dayMidnightTs = useMemo(() => {
    if (!isDailyLayout) return [] as number[];
    const set = new Set<number>();
    dailyDrawSegments.forEach((seg) => set.add(seg.columnNightT));
    const visLeft = visibleMinT ?? 0;
    const visRight = visibleMaxT ?? MINUTES_PER_DAY;
    const startMs = rangeStartMs + visLeft * 60 * 1000;
    const endMs = rangeStartMs + visRight * 60 * 1000;
    const d = new Date(startMs);
    d.setHours(0, 0, 0, 0);
    while (d.getTime() <= endMs) {
      const t = (d.getTime() - rangeStartMs) / (60 * 1000);
      set.add(t);
      d.setDate(d.getDate() + 1);
    }
    return Array.from(set).sort((a, b) => a - b);
  }, [isDailyLayout, dailyDrawSegments, rangeStartMs, visibleMinT, visibleMaxT]);

  const xAxis = Object.values(xAxisMap ?? {})[0] as any;
  const yAxis = Object.values(yAxisMap ?? {})[0] as any;
  if (!xAxis || !yAxis || runs.length === 0) return null;

  const xScale = xAxis.scale;
  const yScale = yAxis.scale;
  const connectorWidth = Math.max(3, BAR_HEIGHT * CONNECTOR_WIDTH_RATIO);
  const blurFilterId = `${chartId}-sleep-blur`;

  if (isDailyLayout) {
    const visLeft = visibleMinT ?? (xScale.domain()?.[0] as number) ?? 0;
    const visRight = visibleMaxT ?? (xScale.domain()?.[1] as number) ?? MINUTES_PER_DAY;

    const columnWidthForDay = (dayMidnightT: number): number => {
      const idx = dayMidnightTs.indexOf(dayMidnightT);
      if (idx < 0) return MIN_DAILY_COLUMN_WIDTH;
      let spanPx = MAX_DAILY_COLUMN_WIDTH;
      if (dayMidnightTs.length > 1) {
        if (idx < dayMidnightTs.length - 1) {
          spanPx = xScale(dayMidnightTs[idx + 1]) - xScale(dayMidnightT);
        } else {
          spanPx = xScale(dayMidnightT) - xScale(dayMidnightTs[idx - 1]);
        }
      } else {
        spanPx = Math.max(
          MIN_DAILY_COLUMN_WIDTH,
          (xScale(visRight) - xScale(visLeft)) * 0.12
        );
      }
      return Math.max(
        MIN_DAILY_COLUMN_WIDTH,
        Math.min(MAX_DAILY_COLUMN_WIDTH, spanPx * DAILY_COLUMN_WIDTH_RATIO)
      );
    };

    const xCenterForNight = (columnNightT: number): number => {
      const nightStartT = columnNightT + SLEEP_EVENING_MINUTES;
      const nightEndT = columnNightT + MINUTES_PER_DAY + SLEEP_MORNING_MINUTES;
      const leftT = Math.max(nightStartT, visLeft);
      const rightT = Math.min(nightEndT, visRight);
      return (xScale(leftT) + xScale(rightT)) / 2;
    };

    return (
      <g className="sleep-stage-layer sleep-stage-daily" shapeRendering="geometricPrecision">
        {dailyDrawSegments.map((seg, index) => {
          const xCenter = xCenterForNight(seg.columnNightT);
          const colW = columnWidthForDay(seg.columnNightT);
          const y0 = yScale(minutesOfDayToSleepWindowY(seg.startMod));
          const y1 = yScale(minutesOfDayToSleepWindowY(seg.endMod));
          const y = Math.min(y0, y1);
          const height = Math.max(Math.abs(y1 - y0), 1);

          return (
            <rect
              key={`sleep-day-seg-${index}`}
              x={xCenter - colW / 2}
              y={y}
              width={colW}
              height={height}
              rx={Math.min(2, colW / 4)}
              ry={Math.min(2, colW / 4)}
              fill={stageColor(seg.value)}
              onMouseEnter={onRunHover ? (event) => onRunHover(seg.run, event) : undefined}
              onMouseMove={onRunHover ? (event) => onRunHover(seg.run, event) : undefined}
              onMouseLeave={onRunHover ? (event) => onRunHover(null, event) : undefined}
            />
          );
        })}
      </g>
    );
  }

  const transitionRuns = showConnectors
    ? runs.filter((run) => run.nextValue != null && run.nextValue !== run.value)
    : [];

  return (
    <g className="sleep-stage-layer" shapeRendering="geometricPrecision">
      <defs>
        <filter
          id={blurFilterId}
          x="-100%"
          y="-100%"
          width="300%"
          height="300%"
          colorInterpolationFilters="sRGB"
        >
          <feGaussianBlur stdDeviation={CONNECTOR_BLUR} />
        </filter>

        {transitionRuns.map((run, index) => {
          const y0 = yScale(run.value);
          const y1 = yScale(run.nextValue as number);
          const { top, height } = connectorSpan(y0, y1);
          const gradientId = `${chartId}-sleep-grad-${index}`;

          return (
            <linearGradient
              key={gradientId}
              id={gradientId}
              gradientUnits="userSpaceOnUse"
              x1={xScale(run.endT)}
              y1={top}
              x2={xScale(run.endT)}
              y2={top + height}
            >
              <stop offset="0%" stopColor={CONNECTOR_COLOR} stopOpacity={0.75} />
              <stop offset="28%" stopColor={CONNECTOR_COLOR} stopOpacity={0.5} />
              <stop offset="50%" stopColor={CONNECTOR_COLOR} stopOpacity={0.35} />
              <stop offset="72%" stopColor={CONNECTOR_COLOR} stopOpacity={0.5} />
              <stop offset="100%" stopColor={CONNECTOR_COLOR} stopOpacity={0.75} />
            </linearGradient>
          );
        })}
      </defs>

      {transitionRuns.map((run, index) => {
        const y0 = yScale(run.value);
        const y1 = yScale(run.nextValue as number);
        const x = xScale(run.endT);
        const { top, height } = connectorSpan(y0, y1);
        if (height <= 0) return null;
        const gradientId = `${chartId}-sleep-grad-${index}`;

        return (
          <rect
            key={`sleep-connector-${index}`}
            x={x - connectorWidth / 2}
            y={top}
            width={connectorWidth}
            height={height}
            rx={connectorWidth / 2}
            ry={connectorWidth / 2}
            fill={`url(#${gradientId})`}
            opacity={0.85}
            filter={`url(#${blurFilterId})`}
          />
        );
      })}

      {runs.map((run, index) => {
        const x0 = xScale(run.startT);
        const x1 = xScale(run.endT);
        const width = Math.max(x1 - x0, MIN_BAR_WIDTH);
        const y = yScale(run.value) - BAR_HEIGHT / 2;
        const x = width === MIN_BAR_WIDTH && x1 - x0 <= 0 ? x0 - MIN_BAR_WIDTH / 2 : x0;

        return (
          <rect
            key={`sleep-bar-${index}`}
            x={x}
            y={y}
            width={width}
            height={BAR_HEIGHT}
            rx={BAR_HEIGHT / 2}
            ry={BAR_HEIGHT / 2}
            fill={stageColor(run.value)}
            onMouseEnter={onRunHover ? (event) => onRunHover(run, event) : undefined}
            onMouseMove={onRunHover ? (event) => onRunHover(run, event) : undefined}
            onMouseLeave={onRunHover ? (event) => onRunHover(null, event) : undefined}
          />
        );
      })}
    </g>
  );
};

export default SleepStageChart;
